import { useEffect } from "react";

export function useVideoEmbeds() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const videoObservers: IntersectionObserver[] = [];
    const wrapperObservers: IntersectionObserver[] = [];
    const interactionHandlers: Array<{ remove: () => void }> = [];

    const urlBuilder = (attributes: any) => {
      const { videoId, thumbnail, controls, autoplay, loop, preload, muted } =
        attributes;
      let baseUrl = `https://iframe.videodelivery.net/${videoId}/iframe?`;
      if (!controls) baseUrl += `&controls=false`;
      if (muted) baseUrl += `&muted=true`;
      if (autoplay) baseUrl += `&autoplay=true`;
      if (loop) baseUrl += `&loop=true`;
      if (preload) baseUrl += `&preload=true`;
      if (thumbnail) baseUrl += `&poster=${thumbnail}`;
      return baseUrl;
    };

    const createIframe = (attributes: any) => {
      const iframe = document.createElement("iframe");
      iframe.title = "Embedded Video";
      iframe.width = "100%";
      iframe.height = "100%";
      iframe.src = urlBuilder(attributes);
      iframe.allow =
        "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture";
      iframe.allowFullscreen = true;
      Object.assign(iframe.style, {
        border: "0",
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
      });
      return iframe;
    };

    const glideGenerateUrl = (value: string | null | undefined) => {
      if (!value) return "";
      const filename = value.split("/").pop() || "";
      const ext = filename.split(".").pop() || "";
      const encoded = btoa(filename).replace(/=/g, "");
      const siteUrl = window.location.origin;
      return `${siteUrl}/img/${encoded}.${ext}`;
    };

    const glideImage = (src: string, args: Record<string, string | number>) => {
      src = glideGenerateUrl(src);
      const parts = src.split(".");
      let result = parts.slice(0, parts.length - 1).join(".");
      Object.keys(args).forEach((key) => {
        result += `-${key}_${args[key]}`;
      });
      result += `.${parts[parts.length - 1]}`;
      return result;
    };

    const processEmbeds = () => {
      const targets = Array.from(
        document.getElementsByClassName("wp-block-cc-video-embed-block"),
      ) as HTMLElement[];

      targets.forEach((target) => {
        if ((target as any).__videoEmbedProcessed) return;
        (target as any).__videoEmbedProcessed = true;

        const video = target.querySelector("video");
        if (video) {
          const loadVideo = (videoEl: HTMLVideoElement) => {
            const source = target.querySelector("source");
            if (!source) return;
            const dataSrc = source.getAttribute("data-src");
            const dataPoster = videoEl.getAttribute("data-poster");
            if (!dataSrc && !dataPoster) return;
            if (dataPoster) {
              videoEl.setAttribute("poster", dataPoster);
              videoEl.removeAttribute("data-poster");
            }
            if (dataSrc) {
              source.setAttribute("src", dataSrc);
              source.removeAttribute("data-src");
            }
            videoEl.load();
          };

          const observer = new IntersectionObserver(
            (entries, inst) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  loadVideo(video);
                  inst.unobserve(video);
                }
              });
            },
            { rootMargin: "400px", threshold: 0 },
          );

          observer.observe(video);
          videoObservers.push(observer);
        }

        const videoWrapper = target.querySelector(
          "[data-videos]",
        ) as HTMLElement | null;
        if (!videoWrapper) return;

        const attributes = JSON.parse(
          videoWrapper.getAttribute("data-videos") || "{}",
        );
        const { videoId, aspectRatio, thumbnail } = attributes;

        const getPaddingTop = () => {
          switch (aspectRatio) {
            case "1:1":
              return "100%";
            case "4:3":
              return "75%";
            case "16:10":
              return "62.5%";
            case "16:9":
            default:
              return "56.25%";
          }
        };

        const skeleton = document.createElement("div");
        Object.assign(skeleton.style, {
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          backgroundColor: "#e0e0e0",
          zIndex: "2",
        });
        videoWrapper.appendChild(skeleton);

        Object.assign(videoWrapper.style, {
          position: "relative",
          overflow: "hidden",
          paddingTop: getPaddingTop(),
        });

        const thumbnailImg = document.createElement("img");
        thumbnailImg.src = thumbnail
          ? glideImage(thumbnail, { w: 720 })
          : `https://customer-yp8wps06c5vlapon.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`;
        Object.assign(thumbnailImg.style, {
          width: "100%",
          height: "100%",
          display: "block",
          transition: "opacity 0.5s ease",
          opacity: "1",
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          objectFit: "cover",
        });
        thumbnailImg.onload = () => {
          thumbnailImg.loading = "lazy";
          thumbnailImg.width = thumbnailImg.naturalWidth;
          thumbnailImg.height = thumbnailImg.naturalHeight;
        };
        videoWrapper.appendChild(thumbnailImg);

        let hasInteracted = false;
        let hasLoaded = false;

        const loadIframe = () => {
          if (hasLoaded) return;
          hasLoaded = true;
          const iframe = createIframe(attributes);
          Object.assign(iframe.style, {
            opacity: "0",
            zIndex: "1",
          });
          videoWrapper.appendChild(iframe);
          iframe.addEventListener("load", () => {
            if (skeleton.parentNode === videoWrapper) {
              videoWrapper.removeChild(skeleton);
            }
            iframe.style.transition = "opacity 0.5s ease";
            iframe.style.opacity = "1";
          });
        };

        const onUserInteraction = () => {
          if (!hasInteracted) {
            hasInteracted = true;
            loadIframe();
            removeListeners();
          }
        };

        const listenerEvents = [
          "scroll",
          "mousemove",
          "touchstart",
          "click",
          "keydown",
        ];

        const addListeners = () => {
          listenerEvents.forEach((event) => {
            window.addEventListener(event, onUserInteraction, { once: true });
          });
          interactionHandlers.push({
            remove: () =>
              listenerEvents.forEach((event) =>
                window.removeEventListener(event, onUserInteraction),
              ),
          });
        };

        const removeListeners = () => {
          interactionHandlers.forEach((h) => h.remove());
        };

        const observer = new IntersectionObserver(
          (entries, inst) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                addListeners();
                inst.unobserve(videoWrapper);
              }
            });
          },
          { rootMargin: "200px", threshold: 0 },
        );

        observer.observe(videoWrapper);
        wrapperObservers.push(observer);
      });
    };

    processEmbeds();

    let rafId: number | null = null;
    const scheduleProcess = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        processEmbeds();
      });
    };

    const mo = new MutationObserver(scheduleProcess);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      mo.disconnect();
      videoObservers.forEach((obs) => obs.disconnect());
      wrapperObservers.forEach((obs) => obs.disconnect());
      interactionHandlers.forEach((h) => h.remove());
    };
  }, []);
}
